import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { ToggleButton, ToggleButtonGroup } from '@mui/material'

import { GRTooltip } from '../../../../components/GRTooltip/GRTooltip'
import { EventLevelTooltipContent, HierarchyLevel } from '../../../live-events/components/EventLevelTooltipContent'
import { CatalogueGranularity } from '../helpers/catalogueUtils'
import { compactToggleGroupSx } from '../helpers/toggleStyles'

interface GranularityToggleProps {
  value: CatalogueGranularity
  onChange: (next: CatalogueGranularity) => void
  // When an event type is selected, the segment has narrowed below the
  // event-category level, so grouping by event category is meaningless.
  eventCategoryLocked: boolean
}

const ORDER: CatalogueGranularity[] = ['eventCategory', 'eventType', 'blueprint']

const LABEL_KEYS: Record<CatalogueGranularity, string> = {
  eventCategory: 'market-analytics:granularity_event_category',
  eventType: 'market-analytics:granularity_event_type',
  blueprint: 'market-analytics:granularity_blueprint',
}

// The same tooltip the Event dialog header shows for each level, with its level subtitle and example path.
const TOOLTIP_LEVELS: Record<CatalogueGranularity, HierarchyLevel> = {
  eventCategory: 'category',
  eventType: 'type',
  blueprint: 'blueprintFamily',
}

const GranularityToggleComponent: React.FC<GranularityToggleProps> = ({ value, onChange, eventCategoryLocked }) => {
  const { t } = useTranslation()

  const handleChange = useCallback(
    (_: React.MouseEvent<HTMLElement>, next: CatalogueGranularity | null) => {
      if (next === null) return
      if (next === 'eventCategory' && eventCategoryLocked) return
      onChange(next)
    },
    [eventCategoryLocked, onChange]
  )

  return (
    <ToggleButtonGroup
      value={value}
      exclusive
      size="small"
      onChange={handleChange}
      aria-label={t('market-analytics:granularity_label')}
      sx={compactToggleGroupSx}
    >
      {ORDER.map((option) => {
        const disabled = option === 'eventCategory' && eventCategoryLocked
        // The lock reason replaces the definition: it is the more useful answer while the option is unavailable.
        const content = disabled ? (
          t('market-analytics:granularity_event_category_locked')
        ) : (
          <EventLevelTooltipContent level={TOOLTIP_LEVELS[option]} showLevelDetails />
        )
        // GRTooltip's wrapper span is what receives hover and focus (bubbled from the button), and it keeps
        // disabled buttons discoverable since they don't fire pointer events. ToggleButtonGroup hands selection,
        // size and position to each ToggleButton through context, so the span does not break the group.
        return (
          <GRTooltip key={option} content={content} styles={{ display: 'inline-flex' }}>
            <ToggleButton value={option} disabled={disabled}>
              {t(LABEL_KEYS[option])}
            </ToggleButton>
          </GRTooltip>
        )
      })}
    </ToggleButtonGroup>
  )
}

export const GranularityToggle = React.memo(GranularityToggleComponent)
GranularityToggle.displayName = 'GranularityToggle'
